import { useEffect, useRef, useState } from 'react';
import { TaskStatus } from '../../types';
import { validateTaskSubmission, ValidatorComment } from '../../services/taskValidator';
import {
  ValidationController,
  ValidationControllerPorts,
} from './validationController';

export interface UseTaskValidationControllerOptions {
  taskId: string | undefined;
  status: TaskStatus | undefined;
  reportError?: (error: Error, taskId: string) => void;
}

const defaultPorts: ValidationControllerPorts = {
  validate: async (taskId: string): Promise<ValidatorComment[]> => (
    (await validateTaskSubmission({ taskId })).comments
  ),
};

export function useTaskValidationController(options: UseTaskValidationControllerOptions) {
  const runtimeRef = useRef<Pick<UseTaskValidationControllerOptions, 'reportError'>>({
    reportError: options.reportError,
  });
  runtimeRef.current = { reportError: options.reportError };

  const portsRef = useRef<ValidationControllerPorts | null>(null);
  if (!portsRef.current) {
    portsRef.current = {
      ...defaultPorts,
      reportError: (error, taskId) => runtimeRef.current.reportError?.(error, taskId),
    };
  }
  const controllerRef = useRef<ValidationController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new ValidationController(portsRef.current);
  }
  const controller = controllerRef.current;
  const [, setVersion] = useState(0);

  useEffect(() => controller.subscribe(() => setVersion((version) => version + 1)), [controller]);

  useEffect(() => {
    controller.initialize();
  }, [controller]);

  useEffect(() => {
    controller.updateContext(options.taskId, options.status);
  }, [controller, options.status, options.taskId]);

  useEffect(() => () => controller.dispose(), [controller]);

  return {
    controller,
    ...controller.getSnapshot(),
    run: () => controller.run(),
    showComments: (comments: ValidatorComment[]) => controller.showComments(comments),
    cancel: () => controller.cancel(),
    dismiss: (fieldPath: string, originalIndex: number) => controller.dismiss(fieldPath, originalIndex),
    restore: (fieldPath: string, originalIndex: number) => controller.restore(fieldPath, originalIndex),
    clear: () => controller.clear(),
  };
}
